import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Fade,
  Collapse,
  Grid,
} from '@mui/material';
import {
  Search as SearchIcon,
  Psychology as AIIcon,
  AutoAwesome as MagicIcon,
  Lightbulb as LightbulbIcon,
  Close as CloseIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useLocation } from '../contexts/LocationContext';
import ActivityCard from './ActivityCard';
import FormattedAIResponse from './FormattedAIResponse';
import { 
  aiSearch, 
  trackAIInteraction, 
  submitAIFeedback, 
  getAISearchSuggestions,
  extractAIInsights,
  formatConfidenceScore,
  isAIFeatureAvailable,
  type AISearchRequest,
  type AISearchResponse,
  type AIAnalysis,
} from '../services/aiApi';
import { Activity, PaginatedActivities } from '../types/Activity';

interface AISearchComponentProps {
  onSearchResults?: (results: PaginatedActivities, query: string, aiAnalysis?: AIAnalysis) => void;
  onActivitySelect?: (activity: Activity) => void;
  placeholder?: string;
  showSuggestions?: boolean;
  compact?: boolean;
  initialQuery?: string;
}

const AISearchComponent: React.FC<AISearchComponentProps> = ({
  onSearchResults,
  onActivitySelect,
  placeholder = "Ask AI: 'I want to learn cooking but I'm terrible at it'",
  showSuggestions = true,
  compact = false,
  initialQuery = '',
}) => {
  const { user } = useAuth();
  const { selectedLocation } = useLocation();
  
  const [query, setQuery] = useState(initialQuery);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<AISearchResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestionsState, setShowSuggestionsState] = useState(showSuggestions);
  // Removed hasLocationInQuery - let AI backend handle location detection
  
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize suggestions
  useEffect(() => {
    if (showSuggestions && isAIFeatureAvailable()) {
      const aiSuggestions = getAISearchSuggestions(selectedLocation || undefined);
      setSuggestions(aiSuggestions.slice(0, 6));
    }
  }, [selectedLocation, showSuggestions]);

  // Clear search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearch = async (searchQuery: string = query) => {
    if (!searchQuery.trim()) {
      setError('Please enter a search query');
      return;
    }

    if (!isAIFeatureAvailable()) {
      setError('AI search is only available for logged-in users');
      return;
    }

    setIsSearching(true);
    setError(null);
    setSearchResults(null);

    try {
      // Let AI backend + OpenAI handle location detection - much smarter than frontend regex
      
      const searchRequest: AISearchRequest = {
        query: searchQuery.trim(),
        // Send only country context, not city - let AI backend handle city expansion
        // This allows backend expansion logic to work properly
        location: selectedLocation ? {
          country: selectedLocation.country,
          // Don't send city - let AI understand city from query or expand from local to country
        } : undefined,
      };

      console.log('🔍 AI Search Request Details:', { 
        query: searchQuery, 
        userLocation: selectedLocation?.city,
        sendingToAI: {
          country: selectedLocation?.country || 'none',
          city: 'none - let AI backend handle everything'
        },
        reason: 'AI backend + OpenAI handle location detection and expansion'
      });
      console.log('🤖 AI Search Request:', searchRequest);

      const response = await aiSearch(searchRequest);
      
      console.log('🤖 AI Search Response:', response);
      console.log('🔍 AI Response Details:', {
        success: response.success,
        activitiesCount: response.data?.activities?.length || 0,
        totalResults: response.data?.total_results || 0,
        conversationResponse: response.data?.conversation_response?.substring(0, 100) + '...',
        aiAnalysis: response.data?.ai_analysis,
        fullResponse: response.data, // Add full response for debugging
      });

      if (response.success) {
        setSearchResults(response);
        setShowAnalysis(true);
        setShowSuggestionsState(false);

        // Convert to PaginatedActivities format for compatibility
        const paginatedResults: PaginatedActivities = {
          count: response.data.total_results || response.data.activities?.length || 0,
          results: response.data.activities || [],
          next: undefined,
          previous: undefined,
        };

        // Notify parent component
        if (onSearchResults) {
          onSearchResults(paginatedResults, searchQuery, response.data.ai_analysis);
        }

        // Track successful search
        if (response.data.search_id) {
          trackAIInteraction({
            search_id: response.data.search_id,
            action: 'search_refined',
          }).catch(console.error);
        }
      } else {
        setError(response.error || 'AI search failed');
      }
    } catch (err) {
      console.error('AI search error:', err);
      setError(err instanceof Error ? err.message : 'Failed to perform AI search');
    } finally {
      setIsSearching(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Auto-search after 2 seconds of no typing (optional)
    if (value.trim().length > 10) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(value);
      }, 2000);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setShowSuggestionsState(false);
    handleSearch(suggestion);
  };


  const handleFeedback = async (rating: 1 | 5, comment?: string) => {
    if (!searchResults?.data.search_id) return;

    try {
      await submitAIFeedback({
        feedback_type: 'search_quality',
        rating,
        comment,
        search_id: searchResults.data.search_id || 0,
      });
      
      console.log('✅ AI feedback submitted');
    } catch (error) {
      console.error('Failed to submit AI feedback:', error);
    }
  };

  const clearSearch = () => {
    setQuery('');
    setSearchResults(null);
    setError(null);
    setShowAnalysis(false);
    setShowSuggestionsState(showSuggestions);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const retrySearch = () => {
    if (query.trim()) {
      handleSearch();
    }
  };

  // Don't render if user is not authenticated
  if (!user || !isAIFeatureAvailable()) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: compact ? 400 : 800, mx: 'auto' }}>
      {/* Search Input */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2, 
          mb: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AIIcon sx={{ color: 'white' }} />
          <Typography variant={compact ? 'body1' : 'h6'} sx={{ fontWeight: 600 }}>
            AI-Powered Search
          </Typography>
          <MagicIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            ref={searchInputRef}
            fullWidth
            value={query}
            onChange={handleInputChange}
            placeholder={placeholder}
            variant="outlined"
            size={compact ? 'small' : 'medium'}
            disabled={isSearching}
            label="AI Search Query"
            aria-label="Enter your activity search query for AI-powered search"
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.9)',
                '& fieldset': {
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255,255,255,0.5)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'white',
                },
              },
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isSearching) {
                handleSearch();
              }
            }}
          />
          
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            disabled={isSearching || !query.trim()}
            sx={{
              minWidth: compact ? 40 : 120,
              backgroundColor: 'rgba(255,255,255,0.2)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            {isSearching ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              <>
                <SearchIcon />
                {!compact && <span style={{ marginLeft: 8 }}>Search</span>}
              </>
            )}
          </Button>

          {(query || searchResults) && (
            <IconButton
              onClick={clearSearch}
              sx={{ color: 'rgba(255,255,255,0.8)' }}
              size={compact ? 'small' : 'medium'}
              aria-label="Clear search"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <IconButton size="small" onClick={retrySearch} aria-label="Retry search">
              <RefreshIcon />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {/* Search Suggestions */}
      <Collapse in={showSuggestionsState && suggestions.length > 0}>
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <LightbulbIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="body2" color="text.secondary">
              Try these AI search examples:
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {suggestions.map((suggestion, index) => (
              <Chip
                key={index}
                label={suggestion}
                variant="outlined"
                size="small"
                onClick={() => handleSuggestionClick(suggestion)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                  },
                }}
              />
            ))}
          </Box>
        </Paper>
      </Collapse>

      {/* AI Analysis */}
      {searchResults && showAnalysis && (
        <Fade in={true}>
          <Card sx={{ mb: 2, border: '1px solid', borderColor: 'primary.light' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AIIcon sx={{ color: 'primary.main' }} />
                <Typography variant="h6" color="primary">
                  AI Analysis
                </Typography>
                <Chip 
                  label={formatConfidenceScore(searchResults.data.ai_analysis?.confidence_score || 0)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>

              {/* No Results Info */}
              {searchResults.data.activities && searchResults.data.activities.length === 0 && (
                <Box sx={{ mb: 2 }}>
                  <Paper sx={{ 
                    p: 2, 
                    bgcolor: 'warning.light', 
                    color: 'warning.contrastText',
                    borderRadius: 1,
                  }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      🔍 <strong>No Activities Found:</strong> 
                      No activities found matching your search criteria
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.8 }}>
                      💡 Try adjusting your search terms or check back later for new activities
                    </Typography>
                  </Paper>
                </Box>
              )}

              {/* Search Scope Info */}
              {searchResults.data.activities && searchResults.data.activities.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Paper sx={{ 
                    p: 2, 
                    bgcolor: 'info.light', 
                    color: 'info.contrastText',
                    borderRadius: 1,
                  }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      🌍 <strong>Search Scope:</strong> 
                      AI analyzed your query and searched for the best matching activities
                    </Typography>
                    {searchResults.data.activities.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          ✅ Found {searchResults.data.activities.length} activities across different locations
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}

              {/* AI Response */}
              {searchResults.data.conversation_response && (
                <Box sx={{ mb: 2 }}>
                  <FormattedAIResponse 
                    text={searchResults.data.conversation_response}
                    variant="default"
                  />
                </Box>
              )}

              {/* Analysis Insights */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Analysis Insights:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {searchResults.data.ai_analysis && extractAIInsights(searchResults.data.ai_analysis).map((insight, index) => (
                    <Chip key={index} label={insight} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>

              {/* Follow-up Questions */}
              {searchResults.data.follow_up_questions && searchResults.data.follow_up_questions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Follow-up questions:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {searchResults.data.follow_up_questions.map((question, index) => (
                      <Chip
                        key={index}
                        label={question}
                        size="small"
                        variant="outlined"
                        onClick={() => handleSuggestionClick(question)}
                        sx={{ 
                          cursor: 'pointer',
                          justifyContent: 'flex-start',
                          '&:hover': {
                            backgroundColor: 'secondary.light',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Feedback */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Was this helpful?
                </Typography>
                <Tooltip title="Good results">
                  <IconButton 
                    size="small" 
                    onClick={() => handleFeedback(5, 'Good AI search results')}
                    sx={{ color: 'success.main' }}
                  >
                    <ThumbUpIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Poor results">
                  <IconButton 
                    size="small" 
                    onClick={() => handleFeedback(1, 'Poor AI search results')}
                    sx={{ color: 'error.main' }}
                  >
                    <ThumbDownIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        </Fade>
      )}

      {/* Activity Results Cards */}
      {searchResults && searchResults.data.activities && searchResults.data.activities.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MagicIcon sx={{ color: 'primary.main' }} />
              AI Search Results ({searchResults.data.activities.length})
            </Typography>
            <Chip 
              label={`🤖 AI-Powered`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
          
          <Grid container spacing={2}>
            {searchResults.data.activities.map((activity, index) => (
              <Grid item xs={12} sm={6} md={4} key={activity.id || index}>
                <Box sx={{ position: 'relative' }}>
                  <ActivityCard 
                    activity={activity}
                    onActivityClick={onActivitySelect}
                  />
                  {/* Location Badge */}
                  {activity.city && activity.city !== selectedLocation?.city && (
                    <Chip
                      label={`📍 ${activity.city}`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        fontSize: '0.75rem',
                        height: 24,
                      }}
                    />
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Results Summary */}
      {searchResults && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Found {searchResults.data.total_results || searchResults.data.activities?.length || 0} activities matching your AI search
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AISearchComponent;

